import { useState, useEffect, useImperativeHandle, useRef } from 'react';
import { calculatePriceFromSkidkaOrMarzha } from './calculationsDiscountOrMarzha';
import { useDelivery } from './useDelivery';
import { calculateDeliveryCostNonmetallic } from '../Productions/calculateDeliveryNonmetallic';

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

// Функция для проверки попадания значения в диапазон
const isInRange = (value, rangeStr) => {
  if (!rangeStr || !value) return false;

  // Заменяем запятые на точки для парсинга
  rangeStr = rangeStr.replace(',', '.');

  // Случай 1: "1-2", "0,7-1,5" - диапазон от-до
  if (rangeStr.includes('-') && !rangeStr.includes('>') && !rangeStr.includes('<')) {
    const [minStr, maxStr] = rangeStr.split('-');
    const min = parseFloat(minStr);
    const max = parseFloat(maxStr);
    return value >= min && value <= max;
  }

  // Случай 2: ">=1", "<=3" - больше или равно / меньше или равно
  if (rangeStr.startsWith('>=')) {
    const min = parseFloat(rangeStr.substring(2));
    return value >= min;
  }
  if (rangeStr.startsWith('<=')) {
    const max = parseFloat(rangeStr.substring(2));
    return value <= max;
  }

  // Случай 3: ">4", "<3" - строго больше / строго меньше
  if (rangeStr.startsWith('>')) {
    const min = parseFloat(rangeStr.substring(1));
    return value > min;
  }
  if (rangeStr.startsWith('<')) {
    const max = parseFloat(rangeStr.substring(1));
    return value < max;
  }

  // Случай 4: "3+", "1-" - больше или равно (варианты записи)
  if (rangeStr.endsWith('+')) {
    const min = parseFloat(rangeStr.substring(0, rangeStr.length - 1));
    return value >= min;
  }

  // Случай 5: "1-2" (как точные значения, не диапазон)
  // Если это не диапазон с дефисом, а точные значения через дефис
  if (rangeStr.includes('-') && !rangeStr.includes('>') && !rangeStr.includes('<')) {
    const values = rangeStr.split('-').map(v => parseFloat(v));
    return values.includes(value);
  }

  return false;
};

// Функция для определения типа песка из названия материала
const getSandType = (materialName) => {
  if (!materialName) return null;
  const name = materialName.toLowerCase();
  if (name.includes('намывной')) return 'namivnoy';
  if (name.includes('сеяный')) return 'seyannyy';
  if (name.includes('карьерный')) return 'karernyy';
  return null;
};

export const useOfferStateNonmetallic = (selectedOffer, addressAndCoordinates, dealId, ref, options, clientName, analogsData, deliveryMethod, setDeliveryMethod, setSelectedOffer, nonmetallicDeliveryMethods, setNonmetallicDeliveryMethods, productsRef, paymentMethod = 'cash') => {
  const [originalMaterials, setOriginalMaterials] = useState([]);
  const [originalDelivery, setOriginalDelivery] = useState(null);
  const [localPrices, setLocalPrices] = useState([]);
  const [localDeliveryPrice, setLocalDeliveryPrice] = useState(0);
  const [localSkidka, setLocalSkidka] = useState([]);
  const [localSkidkaD, setLocalSkidkaD] = useState(null);
  const [localGlobalSkidka, setLocalGlobalSkidka] = useState('');
  const [per, setPer] = useState({ id: 1, text: 'Скидка' });
  // Для нерудки аналоги не используются
  const [analogs] = useState([]);
  const [activeOffers, setActiveOffers] = useState({
    original: null,
  });
  const [responseText] = useState('');
  const [analogDeliveries] = useState({});
  const [buttonStates] = useState({});
  const [notification, setNotification] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, text: "", x: 0, y: 0, targetElement: null });
  const [additionalMaterials, setAdditionalMaterials] = useState([]);
  // Состояние для способов доставки каждого материала (расширенные данные)
  const [materialDeliveryMethods, setMaterialDeliveryMethods] = useState({});
  // Состояние для данных о доставке каждого материала (стоимость, рейсы и т.д.)
  const [materialDeliveryData, setMaterialDeliveryData] = useState({});
  // Флаг для предотвращения повторного добавления материала при удалении
  const [isRemoving, setIsRemoving] = useState(false);
  // Список недавно удаленных материалов (для предотвращения повторного добавления)
  const [recentlyRemovedMaterials, setRecentlyRemovedMaterials] = useState([]);
  // Состояние для аналогов песка (объект: { [materialName]: analogs[] })
  const [sandAnalogs, setSandAnalogs] = useState({});
  const [isLoadingAnalogs, setIsLoadingAnalogs] = useState({});

  const localRef = useRef(null);

  const { recalculateDelivery } = useDelivery(addressAndCoordinates);

  // Функция для пересчета доставки нерудки с новым методом
  const recalculateNonmetallicDelivery = async (materials, newDeliveryMethod, productionInfo) => {
    try {
      console.log('🔄 Пересчет доставки нерудки с методом:', newDeliveryMethod);

      // Получаем информацию о производстве
      const productionData = selectedOffer?.productionInfo || productionInfo;

      // Вызываем специальную функцию для нерудки
      const deliveryResult = await calculateDeliveryCostNonmetallic(
        materials,
        newDeliveryMethod,
        productionData,
        false, // selfPickUp
        addressAndCoordinates,
        selectedOffer?.region || 'СПБ', // selectedRegion
        paymentMethod || 'cash'
      );

      console.log('📦 Результат пересчета доставки нерудки:', deliveryResult);

      // Извлекаем данные из результата
      if (deliveryResult?.ner?.details?.data) {
        const deliveryData = deliveryResult.ner.details.data;
        return {
          totalCost: deliveryData.totalCost || 0,
          numberOfTrips: deliveryData.numberOfTrips || 0,
          costPerTrip: deliveryData.costPerTrip || 0,
          bestMethod: newDeliveryMethod
        };
      } else if (deliveryResult?.ner?.details && Array.isArray(deliveryResult.ner.details)) {
        // Если данные в массиве, берем первый элемент
        const firstDeliveryGroup = deliveryResult.ner.details[0];
        if (firstDeliveryGroup?.deliveryData?.data) {
          const deliveryData = firstDeliveryGroup.deliveryData.data;
          return {
            totalCost: deliveryData.totalCost || 0,
            numberOfTrips: deliveryData.numberOfTrips || 0,
            costPerTrip: deliveryData.costPerTrip || 0,
            bestMethod: newDeliveryMethod
          };
        }
      }

      console.warn('⚠️ Не удалось извлечь данные доставки из результата');
      return null;

    } catch (error) {
      console.error('❌ Ошибка при пересчете доставки нерудки:', error);
      return null;
    }
  };

  // Функция поиска аналогов песка
  const findAnalogsForSand = async (currentMaterial) => {
    if (!currentMaterial || !currentMaterial.name) return [];

    // Проверяем, что это песок
    if (!currentMaterial.category?.includes('Песок')) {
      console.log('Материал не является песком, аналоги не ищем');
      return [];
    }

    // Проверяем наличие и валидность параметров mkr, kf для поиска аналогов
    // ВАЖНО: заменяем запятые на точки перед parseFloat!
    const currentMkr = parseFloat(String(currentMaterial.mkr || '').replace(',', '.'));
    const currentKf = parseFloat(String(currentMaterial.kf || '').replace(',', '.'));
    const currentClass = currentMaterial.class;

    if (isNaN(currentMkr) || isNaN(currentKf)) {
      console.log('⚠️ У материала отсутствуют валидные параметры mkr или kf для поиска аналогов:', {
        mkr: currentMaterial.mkr,
        kf: currentMaterial.kf,
        name: currentMaterial.name,
        currentMkr,
        currentKf
      });
      return [];
    }

    const materialName = currentMaterial.name;
    console.log(`🔍 Поиск аналогов: ${materialName}`);
    console.log(`   Исходные данные: mkr="${currentMaterial.mkr}", kf="${currentMaterial.kf}"`);
    console.log(`   После парсинга: mkr=${currentMkr}, kf=${currentKf}, производитель=${currentMaterial.manufacturer}`);

    setIsLoadingAnalogs(prev => ({ ...prev, [materialName]: true }));

    try {
      // Загружаем данные из кэша (получаем с сервера)
      const response = await fetch(`${PATH_TO_BACKEND}/api/get-analogs-data`, {
        credentials: 'include'
      });
      const result = await response.json();

      if (!result.success || !result.data) {
        console.error('Ошибка получения данных для аналогов:', result);
        return [];
      }

      const stockSheet = result.data;
      const sandCharacteristics = result.sandCharacteristics || [];
      const productionsMap = result.productions || {};
      const { quantity, unit } = currentMaterial;

      console.log(`🔍 Загружено данных: stockSheet=${stockSheet.length}, sandCharacteristics=${sandCharacteristics.length}`);

      // Отладочное логирование первых записей sand_characteristics
      if (sandCharacteristics.length > 0) {
        console.log(`📋 Пример данных sand_characteristics (первые 2 записи):`, sandCharacteristics.slice(0, 2));
      }

      // Определяем тип песка из названия
      const sandType = getSandType(currentMaterial.name);
      if (!sandType) {
        console.log('⚠️ Не удалось определить тип песка:', currentMaterial.name);
        return [];
      }

      console.log(`🔍 Тип песка: ${sandType}`);

      // Шаг 1: Находим ВСЕ подходящие записи в sand_characteristics
      // Ищем записи где текущие mkr, kf, class попадают в диапазоны
      const matchingSandCharacteristics = [];

      for (const sc of sandCharacteristics) {
        // Проверяем тип песка
        const scType = sc.NAME.toLowerCase().includes('намывной') ? 'namivnoy' :
                       sc.NAME.toLowerCase().includes('сеянный') ? 'seyannyy' :
                       sc.NAME.toLowerCase().includes('карьерный') ? 'karernyy' : null;

        if (scType !== sandType) continue;

        // Проверяем попадание в диапазоны
        const mkrMatches = !sc.RANGE_MKR || isInRange(currentMkr, sc.RANGE_MKR);
        const kfMatches = !sc.KF || isInRange(currentKf, sc.KF);
        const classMatches = !sc.CLASS_VAL || sc.CLASS_VAL === currentClass || isInRange(parseFloat(currentClass), sc.CLASS_VAL);

        // Отладочное логирование
        console.log(`🔍 Проверяем диапазоны: мкр=${currentMkr} попадает в "${sc.RANGE_MKR}"? ${mkrMatches}, кф=${currentKf} попадает в "${sc.KF}"? ${kfMatches}`);

        if (mkrMatches && kfMatches && classMatches) {
          matchingSandCharacteristics.push(sc);
          console.log(`✅ Найдена подходящая характеристика песка (id=${sc.ID}):`, sc);
        }
      }

      if (matchingSandCharacteristics.length === 0) {
        console.log('⚠️ Не найдены подходящие характеристики песка в sand_characteristics');
        return [];
      }

      console.log(`📋 Найдено ${matchingSandCharacteristics.length} подходящих записей`);

      // Шаг 2: Формируем критерии поиска аналогов из ВСЕХ записей
      // Берем ВСЕ доступные типы аналогов (намывной, сеянный, карьерный)
      const analogCriteria = [
        { prefix: 'NAMIVNOY', name: 'намывной' },
        { prefix: 'SEYANYY', name: 'сеянный' },
        { prefix: 'KARERNY', name: 'карьерный' }
      ];

      const searchCriteria = [];

      // Для каждой записи формируем критерии
      matchingSandCharacteristics.forEach((sc, index) => {
        console.log(`📋 Запись ${index + 1} (id=${sc.ID}):`);
        analogCriteria.forEach(criterion => {
          const mkr = sc[`MKR_${criterion.prefix}`];
          const kf = sc[`KF_${criterion.prefix}`];
          const classVal = sc[`CLASS_${criterion.prefix}`];

          if (mkr || kf || classVal) {
            const criteria = {
              type: criterion.name,
              mkr: mkr,
              kf: kf,
              class: classVal,
              sourceRecordId: sc.ID // Добавляем ID источника записи
            };
            searchCriteria.push(criteria);
            console.log(`   Критерий (${criterion.name}): мкр=${mkr}, кф=${kf}, класс=${classVal}`);
          }
        });
      });

      if (searchCriteria.length === 0) {
        console.log('⚠️ Нет критериев для поиска аналогов');
        return [];
      }

      console.log(`📋 Всего критериев для поиска: ${searchCriteria.length}`);

      // Шаг 3: Ищем потенциальные аналоги в stockSheet по ВСЕМ критериям
      const potentialAnalogs = stockSheet.filter(item => {
        // Исключаем текущий материал ТОЛЬКО если ПОЛНОЕ совпадение производителя
        const isSameManufacturer = item.MANUFACTURER === currentMaterial.manufacturer;

        // Получаем значения mkr и kf из потенциального аналога
        // ВАЖНО: заменяем запятые на точки перед parseFloat!
        const itemMkr = parseFloat(String(item.MKR || '').replace(',', '.'));
        const itemKf = parseFloat(String(item.KF || '').replace(',', '.'));

        // Проверяем соответствие хотя бы одному критерию поиска
        const matchesAnyCriteria = searchCriteria.some(criteria => {
          // Проверяем, что значения аналога являются валидными числами
          const hasValidItemValues = !isNaN(itemMkr) && !isNaN(itemKf);

          // Ищем аналоги по mkr и kf >= критериям
          const mkrMatch = !criteria.mkr || (hasValidItemValues && isInRange(itemMkr, criteria.mkr));
          const kfMatch = !criteria.kf || (hasValidItemValues && isInRange(itemKf, criteria.kf));
          const classMatch = !criteria.class || (item.CLASS && item.CLASS === criteria.class);

          // Проверяем что это песок
          const isSand = item.NOMENCLATURE && getSandType(item.NOMENCLATURE);

          return mkrMatch && kfMatch && classMatch && isSand && !isSameManufacturer;
        });

        // Если у текущего материала есть доставка (не самовывоз), исключаем аналоги с самовывозом
        const hasCurrentDelivery = currentMaterial.realDeliveryMethod && currentMaterial.realDeliveryMethod !== 'Самовывоз';
        const analogProductionInfo = productionsMap[item.MANUFACTURER] || {};
        const hasAnalogDelivery = analogProductionInfo.LATITUDE && analogProductionInfo.LONGITUDE;

        // Если у текущего есть доставка, но у аналога нет - исключаем
        const deliveryMatch = !hasCurrentDelivery || hasAnalogDelivery;

        return matchesAnyCriteria && deliveryMatch;
      });

      if (potentialAnalogs.length === 0) {
        console.log('⚠️ Аналоги не найдены');
        return [];
      }

      // Логируем найденные аналоги
      potentialAnalogs.forEach((analog, idx) => {
        console.log(`🔍 Аналог ${idx + 1}: ${analog.MANUFACTURER}, мкр=${analog.MKR}, кф=${analog.KF}, цена: ${analog.PRICE_CASH || analog.PRICE_NONCASH}`);
      });

      // Шаг 4: Для каждого аналога рассчитываем полную стоимость (материал + доставка)
      const analogsWithFullCost = await Promise.all(
        potentialAnalogs.map(async (item) => {
          const pricePerOne = item.PRICE_CASH || item.PRICE_NONCASH;
          if (!pricePerOne) return null; // Пропускаем если нет цены

          const materialCost = pricePerOne * quantity;

          // Получаем productionInfo для производства аналога
          const analogProductionInfo = productionsMap[item.MANUFACTURER] || {};

          // Рассчитываем доставку для аналога
          const analogMaterial = {
            name: item.NOMENCLATURE,
            quantity,
            unit,
            manufacturer: item.MANUFACTURER,
            massPerOne: item.WEIGHT_TONS || 0,
            volumePerOne: item.VOLUME_CUBIC || 1,
          };

          let deliveryCost = 0;
          let deliveryMethodName = 'Самовывоз';

          // Только если есть координаты производства и адрес доставки
          if (analogProductionInfo.LATITUDE && analogProductionInfo.LONGITUDE && addressAndCoordinates) {
            try {
              // Используем способ доставки текущего материала
              const analogDeliveryMethod = currentMaterial.realDeliveryMethod || currentMaterial.deliveryMethod || 'Оптимальный маршрут';

              const deliveryResult = await calculateDeliveryCostNonmetallic(
                [analogMaterial],
                analogDeliveryMethod,
                analogProductionInfo, // productionInfo аналога
                false, // selfPickUp
                addressAndCoordinates,
                item.REGION,
                paymentMethod || 'cash'
              );

              // Извлекаем стоимость доставки из результата
              if (deliveryResult?.ner?.details?.data) {
                deliveryCost = deliveryResult.ner.details.data.totalCost || 0;
                deliveryMethodName = deliveryResult.ner.details.data.bestMethod || 'Самовывоз';
              } else if (deliveryResult?.ner?.details && Array.isArray(deliveryResult.ner.details)) {
                const firstDeliveryGroup = deliveryResult.ner.details[0];
                if (firstDeliveryGroup?.deliveryData?.data) {
                  deliveryCost = firstDeliveryGroup.deliveryData.data.totalCost || 0;
                  deliveryMethodName = firstDeliveryGroup.deliveryData.data.bestMethod || 'Самовывоз';
                }
              }
            } catch (err) {
              console.warn(`⚠️ Ошибка расчета доставки для ${item.MANUFACTURER}:`, err);
            }
          }

          const totalCost = materialCost + deliveryCost;

          console.log(`💰 Аналог ${item.MANUFACTURER}: материал=${materialCost}, доставка=${deliveryCost}, итого=${totalCost}`);

          return {
            name: item.NOMENCLATURE,
            nomenclature: item.NOMENCLATURE,
            manufacturer: item.MANUFACTURER,
            pricePerOne,
            materialCost,
            deliveryCost,
            totalCost,
            quantity,
            unit,
            mkr: item.MKR,
            kf: item.KF,
            class: item.CLASS,
            source: item.SOURCE,
            region: item.REGION,
            deliveryMethod: deliveryMethodName,
            isCurrentDelivery: false,
          };
        })
      );

      // Фильтруем null значения
      const validAnalogs = analogsWithFullCost.filter(analog => analog !== null);

      // Шаг 5: Фильтруем только те аналоги, которые дешевле текущего предложения
      const currentTotalCost = currentMaterial.sum || (currentMaterial.pricePerOne * quantity) + (currentMaterial.delivery?.totalCost || 0);
      console.log(`💰 Текущая полная стоимость: ${currentTotalCost} р. (материал: ${currentMaterial.pricePerOne * quantity}, доставка: ${currentMaterial.delivery?.totalCost || 0})`);

      const cheaperAnalogs = validAnalogs.filter(analog => analog.totalCost < currentTotalCost);

      console.log(`💰 ${cheaperAnalogs.length} аналогов дешевле текущего предложения (${currentTotalCost} р.)`);

      // Логируем результаты фильтрации
      validAnalogs.forEach((analog, idx) => {
        const isCheaper = analog.totalCost < currentTotalCost;
        console.log(`  ${idx + 1}. ${analog.manufacturer}: ${analog.totalCost} р. ${isCheaper ? '✓' : '✗'} (${isCheaper ? 'дешевле' : 'дороже'})`);
      });

      // Шаг 6: Сортируем по итоговой стоимости (от дешевого к дорогому)
      const sortedAnalogs = cheaperAnalogs.sort((a, b) => a.totalCost - b.totalCost);

      // Обновляем состояние аналогов для конкретного материала
      setSandAnalogs(prev => ({ ...prev, [materialName]: sortedAnalogs }));

      return sortedAnalogs;

    } catch (error) {
      console.error('❌ Ошибка при поиске аналогов:', error);
      setSandAnalogs(prev => ({ ...prev, [materialName]: [] }));
      return [];
    } finally {
      setIsLoadingAnalogs(prev => ({ ...prev, [materialName]: false }));
    }
  };

  // Функция выбора аналога
  const handleSelectAnalog = async (selectedAnalog, originalMaterialName) => {
    console.log('✅ Выбран аналог:', selectedAnalog);
    console.log('📦 Заменяем материал:', originalMaterialName);

    if (!selectedOffer || !selectedOffer.materials) {
      console.error('❌ selectedOffer не определен');
      return;
    }

    // Находим индекс материала, который нужно заменить
    const materialIndex = selectedOffer.materials.findIndex(m => m.name === originalMaterialName);
    if (materialIndex === -1) {
      console.error('❌ Материал не найден:', originalMaterialName);
      return;
    }

    const oldMaterial = selectedOffer.materials[materialIndex];

    // Создаем новый материал на основе аналога
    const newMaterial = {
      ...oldMaterial,
      name: selectedAnalog.name,
      nomenclature: selectedAnalog.nomenclature || selectedAnalog.name,
      manufacturer: selectedAnalog.manufacturer,
      pricePerOne: selectedAnalog.quantity > 0 ? Math.ceil(selectedAnalog.totalCost / selectedAnalog.quantity) : selectedAnalog.pricePerOne,
      basePricePerOne: selectedAnalog.pricePerOne, // Базовая цена материала без доставки
      sum: selectedAnalog.totalCost,
      quantity: selectedAnalog.quantity,
      unit: selectedAnalog.unit,
      mkr: selectedAnalog.mkr,
      kf: selectedAnalog.kf,
      class: selectedAnalog.class,
      source: selectedAnalog.source,
      region: selectedAnalog.region,
      realDeliveryMethod: selectedAnalog.deliveryMethod,
      // Сохраняем информацию о стоимости доставки
      materialCost: selectedAnalog.materialCost,
      deliveryCost: selectedAnalog.deliveryCost,
    };

    console.log('📦 Новый материал:', newMaterial);

    // Обновляем selectedOffer
    if (setSelectedOffer) {
      setSelectedOffer(prevOffer => {
        if (!prevOffer || !prevOffer.materials) return prevOffer;

        const newMaterials = [...prevOffer.materials];
        newMaterials[materialIndex] = newMaterial;

        // Пересчитываем общую стоимость доставки
        const newDelivery = {
          ...prevOffer.delivery,
          totalCost: selectedAnalog.deliveryCost,
          name: selectedAnalog.deliveryMethod || 'Самовывоз',
        };

        return {
          ...prevOffer,
          production: selectedAnalog.manufacturer,
          materials: newMaterials,
          delivery: newDelivery,
        };
      });
    }

    // Обновляем локальные цены
    setLocalPrices(prev => {
      const newPrices = [...prev];
      newPrices[materialIndex] = newMaterial.pricePerOne;
      return newPrices;
    });

    // Очищаем аналоги для этого материала (скрываем список)
    setSandAnalogs(prev => {
      const updated = { ...prev };
      delete updated[originalMaterialName];
      return updated;
    });

    console.log('✅ Материал успешно заменен');
  };

  useImperativeHandle(ref, () => localRef.current);

  // Очистка списка недавно удаленных материалов через 5 секунд
  useEffect(() => {
    if (recentlyRemovedMaterials.length > 0) {
      const timer = setTimeout(() => {
        setRecentlyRemovedMaterials(prev => {
          const now = Date.now();
          const filtered = prev.filter(removed => (now - removed.timestamp) < 5000);
          if (filtered.length !== prev.length) {
            console.log('🧹 Очищен список недавно удаленных материалов:', prev.length - filtered.length, 'удалено');
          }
          return filtered;
        });
      }, 1000); // Проверяем каждую секунду

      return () => clearTimeout(timer);
    }
  }, [recentlyRemovedMaterials]);

  // НЕ синхронизируем materialDeliveryMethods с nonmetallicDeliveryMethods
  // Синхронизация работает только в одну сторону: от OfferTableNonmetallic к ProductionsNonmetallic

  // Обновляем состояние аналогов песка при изменении selectedOffer
  useEffect(() => {
    if (!selectedOffer || !selectedOffer.materials || selectedOffer.materials.length === 0) {
      setSandAnalogs({});
      return;
    }

    // Ищем песок среди материалов
    const sandMaterial = selectedOffer.materials.find(material =>
      material.category?.includes('Песок') ||
      material.name?.toLowerCase().includes('песок')
    );

    const currentMkr = parseFloat(String(sandMaterial?.mkr || '').replace(',', '.'));
    const currentKf = parseFloat(String(sandMaterial?.kf || '').replace(',', '.'));

    if (sandMaterial && !isNaN(currentMkr) && !isNaN(currentKf)) {
      // Создаем ключ из параметров для сравнения
      const currentKey = `${sandMaterial.name}_${sandMaterial.manufacturer}_${sandMaterial.mkr}_${sandMaterial.kf}`;

      console.log('🔍 Проверка необходимости поиска аналогов:', currentKey);

      // Очищаем старые аналоги и ищем новые
      setSandAnalogs({});
      findAnalogsForSand(sandMaterial);
    } else {
      setSandAnalogs({});
    }
  }, [
    selectedOffer?.production,
    // Отслеживаем изменение параметров песка
    selectedOffer?.materials?.find(m => m.name?.toLowerCase().includes('песок'))?.name,
    selectedOffer?.materials?.find(m => m.name?.toLowerCase().includes('песок'))?.manufacturer,
    selectedOffer?.materials?.find(m => m.name?.toLowerCase().includes('песок'))?.mkr,
    selectedOffer?.materials?.find(m => m.name?.toLowerCase().includes('песок'))?.kf,
  ]);


  useEffect(() => {
    if (selectedOffer && selectedOffer.materials && !isRemoving) {
      console.log('✅ selectedOffer обновлен (Нерудка):', selectedOffer);

      // Сначала обновляем материалы, затем синхронно пересчитываем массивы цен и скидок
      const newMaterials = selectedOffer.materials || [];
      const newPrices = newMaterials.map((material) => material.pricePerOne || 0);
      const newSkidkaValues = newMaterials.map(() => '');

      setOriginalMaterials(prevMaterials => {
        console.log('🔍 Проверка обновления материалов:', {
          newMaterialsLength: newMaterials.length,
          prevMaterialsLength: prevMaterials.length,
          isRemoving,
          newMaterials: newMaterials.map(m => m.name),
          prevMaterials: prevMaterials.map(m => m.name)
        });

        // Для повторного выбора с той же вкладки нужно заменить только материалы той же номенклатуры
        // Определяем, какие номенклатуры приходят в новом предложении
        const newNomenclatures = [...new Set(newMaterials.map(m => m.name))];

        // Проверяем, есть ли уже такие номенклатуры в существующих материалах
        const existingNomenclatures = prevMaterials.map(m => m.name);
        const hasMatchingNomenclatures = newNomenclatures.some(name => existingNomenclatures.includes(name));

        if (hasMatchingNomenclatures) {
          console.log('🔄 Замена материалов для номенклатур:', newNomenclatures);

          // Удаляем старые материалы с теми же номенклатурами
          const materialsWithoutReplacedNomenclatures = prevMaterials.filter(prevMat =>
            !newNomenclatures.includes(prevMat.name)
          );

          // Добавляем новые материалы
          const updatedMaterials = [...materialsWithoutReplacedNomenclatures, ...newMaterials];

          // Сохраняем способ доставки для новых материалов при замене
          newMaterials.forEach(material => {
            const currentDeliveryMethod = nonmetallicDeliveryMethods[material.name] ||
                                         selectedOffer?.delivery?.name ||
                                         'Оптимальный маршрут';
            const currentDeliveryData = {
              name: currentDeliveryMethod,
              totalCost: selectedOffer?.delivery?.totalCost || 0,
              numberOfTrips: selectedOffer?.delivery?.numberOfTrips || 0,
              costPerTrip: selectedOffer?.delivery?.costPerTrip || 0
            };

            setMaterialDeliveryMethods(prev => ({
              ...prev,
              [material.name]: currentDeliveryMethod
            }));

            setMaterialDeliveryData(prev => ({
              ...prev,
              [material.name]: currentDeliveryData
            }));

            console.log(`🔄 Обновлен способ доставки для ${material.name}:`, currentDeliveryData);
          });

          // Пересчитываем массивы цен и скидок
          const updatedPrices = updatedMaterials.map((material) => {
            // Если это новый материал
            if (newNomenclatures.includes(material.name)) {
              return material.pricePerOne || 0;
            }
            // Сохраняем старые цены для остальных материалов
            const oldIndex = prevMaterials.findIndex(m => m.name === material.name);
            return oldIndex !== -1 ? (localPrices[oldIndex] || material.pricePerOne || 0) : (material.pricePerOne || 0);
          });

          const updatedSkidkas = updatedMaterials.map((material) => {
            // Если это новый материал
            if (newNomenclatures.includes(material.name)) {
              return '';
            }
            // Сохраняем старые скидки для остальных материалов
            const oldIndex = prevMaterials.findIndex(m => m.name === material.name);
            return oldIndex !== -1 ? (localSkidka[oldIndex] || '') : '';
          });

          setLocalPrices(updatedPrices);
          setLocalSkidka(updatedSkidkas);

          return updatedMaterials;
        }

        // Если нет новых материалов, ничего не делаем
        if (newMaterials.length === 0) {
          console.log('🚫 Пропускаем обновление - нет материалов в selectedOffer');
          return prevMaterials;
        }

        // Проверяем, не были ли эти материалы недавно удалены
        const materialsToAdd = newMaterials.filter(newMaterial =>
          !recentlyRemovedMaterials.some(removed =>
            removed.name === newMaterial.name &&
            (removed.manufacturer === newMaterial.manufacturer || removed.source === newMaterial.source)
          )
        );

        if (materialsToAdd.length === 0) {
          console.log('🚫 Все материалы были недавно удалены, пропускаем обновление');
          return prevMaterials;
        }

        // Добавляем новые материалы к существующим
        console.log(`➕ Добавляем новые материалы:`, materialsToAdd.map(m => m.name));
        const updatedMaterials = [...prevMaterials, ...materialsToAdd];

        // Сохраняем способ доставки для новых материалов
        materialsToAdd.forEach(material => {
          const currentDeliveryMethod = nonmetallicDeliveryMethods[material.name] ||
                                       selectedOffer?.delivery?.name ||
                                       'Оптимальный маршрут';
          const currentDeliveryData = {
            name: currentDeliveryMethod,
            totalCost: selectedOffer?.delivery?.totalCost || 0,
            numberOfTrips: selectedOffer?.delivery?.numberOfTrips || 0,
            costPerTrip: selectedOffer?.delivery?.costPerTrip || 0
          };

          setMaterialDeliveryMethods(prev => ({
            ...prev,
            [material.name]: currentDeliveryMethod
          }));

          setMaterialDeliveryData(prev => ({
            ...prev,
            [material.name]: currentDeliveryData
          }));

          console.log(`🚚 Сохранен способ доставки для ${material.name}:`, currentDeliveryData);
        });

        // Пересчитываем массивы цен и скидок
        const updatedPrices = updatedMaterials.map((material) => {
          // Если это новый материал
          if (materialsToAdd.some(newMat => newMat.name === material.name)) {
            return material.pricePerOne || 0;
          }
          // Сохраняем старые цены для остальных материалов
          const oldIndex = prevMaterials.findIndex(m => m.name === material.name);
          return oldIndex !== -1 ? (localPrices[oldIndex] || material.pricePerOne || 0) : (material.pricePerOne || 0);
        });

        const updatedSkidkas = updatedMaterials.map((material) => {
          // Если это новый материал
          if (materialsToAdd.some(newMat => newMat.name === material.name)) {
            return '';
          }
          // Сохраняем старые скидки для остальных материалов
          const oldIndex = prevMaterials.findIndex(m => m.name === material.name);
          return oldIndex !== -1 ? (localSkidka[oldIndex] || '') : '';
        });

        setLocalPrices(updatedPrices);
        setLocalSkidka(updatedSkidkas);

        return updatedMaterials;
      });

      setOriginalDelivery(selectedOffer.delivery || null);

      setLocalDeliveryPrice(0); // Доставка включена в стоимость для нерудки
      setLocalSkidkaD('');
      setLocalGlobalSkidka('');
    }
  }, [selectedOffer, isRemoving]);

  // Отдельный useEffect для создания активного предложения из объединенных материалов
  useEffect(() => {
    console.log('🔄 useEffect activeOffers проверка:', {
      originalMaterialsLength: originalMaterials.length,
      localPricesLength: localPrices.length,
      isRemoving,
      shouldCreate: originalMaterials.length > 0 && localPrices.length > 0
    });

    if (originalMaterials.length > 0 && localPrices.length > 0) {
      console.log('🔄 Пересоздаем активное предложение для объединенных материалов');

      const originalOffer = {
        production: "Объединенное предложение", // Или можно взять производство из последнего выбранного
        materials: originalMaterials.map((material, index) => ({
          ...material,
          pricePerOne: localPrices[index] || material.pricePerOne || 0,
          sum: Math.ceil((localPrices[index] || material.pricePerOne || 0) * material.quantity),
          // Добавляем индивидуальный способ доставки для каждого материала
          deliveryMethod: materialDeliveryMethods[material.name] ||
                         nonmetallicDeliveryMethods[material.name] ||
                         'Оптимальный маршрут',
          // Добавляем данные о доставке
          deliveryData: materialDeliveryData[material.name] || {
            name: materialDeliveryMethods[material.name] || 'Оптимальный маршрут',
            totalCost: 0,
            numberOfTrips: 0,
            costPerTrip: 0
          },
        })),
        delivery: {
          name: 'Самовывоз', // Общий метод не используется
          totalCost: 0, // Считается индивидуально для каждого материала
          numberOfTrips: 0,
          costPerTrip: 0
        },
        date: new Date().toLocaleDateString('ru-RU'),
        // Специальный флаг для нерудки
        isNonmetallic: true,
      };

      setActiveOffers(prev => ({
        ...prev,
        original: originalOffer,
      }));

      console.log('✅ Активное предложение для объединенных материалов создано:', originalOffer);
    }
  }, [originalMaterials, localPrices, materialDeliveryMethods, materialDeliveryData, nonmetallicDeliveryMethods]);

  const handleMaterialPriceChange = (index, newPrice) => {
    const numericPrice = Number(newPrice) || 0;

    setLocalPrices(prevPrices => {
      const updatedPrices = [...prevPrices];
      updatedPrices[index] = numericPrice;
      return updatedPrices;
    });

    // Обновляем активное предложение
    setActiveOffers(prev => {
      if (!prev.original) return prev;

      const updatedMaterials = [...prev.original.materials];
      updatedMaterials[index] = {
        ...updatedMaterials[index],
        pricePerOne: numericPrice,
        sum: Math.ceil(numericPrice * updatedMaterials[index].quantity),
      };

      return {
        ...prev,
        original: {
          ...prev.original,
          materials: updatedMaterials,
        },
      };
    });

    // Сбрасываем скидку для этого материала
    setLocalSkidka(prevSkidka => {
      const updatedSkidka = [...prevSkidka];
      updatedSkidka[index] = '';
      return updatedSkidka;
    });

    console.log(`💰 Цена материала ${index} изменена на ${numericPrice} (Нерудка)`);
  };

  const handleSkidkaChange = (index, newSkidka) => {
    console.log(`📉 handleSkidkaChange: index=${index}, newSkidka=${newSkidka} (Нерудка)`);

    const originalPrice = originalMaterials[index]?.pricePerOne || 0;
    const newPrice = calculatePriceFromSkidkaOrMarzha(originalPrice, newSkidka, per.id === 2);

    setLocalSkidka(prevSkidka => {
      const updatedSkidka = [...prevSkidka];
      updatedSkidka[index] = newSkidka;
      return updatedSkidka;
    });

    setLocalPrices(prevPrices => {
      const updatedPrices = [...prevPrices];
      updatedPrices[index] = newPrice;
      return updatedPrices;
    });

    // Обновляем активное предложение
    setActiveOffers(prev => {
      if (!prev.original) return prev;

      const updatedMaterials = [...prev.original.materials];
      updatedMaterials[index] = {
        ...updatedMaterials[index],
        pricePerOne: newPrice,
        sum: Math.ceil(newPrice * updatedMaterials[index].quantity),
      };

      return {
        ...prev,
        original: {
          ...prev.original,
          materials: updatedMaterials,
        },
      };
    });
  };

  const handleDeliveryPriceChange = (newPrice) => {
    const numericPrice = Number(newPrice) || 0;
    setLocalDeliveryPrice(numericPrice);

    // Обновляем активное предложение
    setActiveOffers(prev => {
      if (!prev.original) return prev;

      return {
        ...prev,
        original: {
          ...prev.original,
          delivery: {
            ...prev.original.delivery,
            totalCost: numericPrice,
          },
        },
      };
    });

    // Сбрасываем скидку на доставку
    setLocalSkidkaD('');
    console.log(`🚚 Цена доставки изменена на ${numericPrice} (Нерудка)`);
  };

  const handleDeliveryUpdate = async (productionName) => {
    console.log('🔄 handleDeliveryUpdate для нерудки:', productionName);

    try {
      const materials = activeOffers.original?.materials || [];
      if (materials.length === 0) {
        console.warn('⚠️ Нет материалов для пересчета доставки');
        return;
      }

      // Для нерудки используем специальную функцию пересчета
      const currentDeliveryMethod = selectedOffer?.delivery?.name || deliveryMethod || 'Оптимальный маршрут';
      const deliveryData = await recalculateNonmetallicDelivery(materials, currentDeliveryMethod, productionName);

      if (deliveryData && deliveryData.totalCost !== undefined) {
        setLocalDeliveryPrice(deliveryData.totalCost);

        setActiveOffers(prev => {
          if (!prev.original) return prev;

          return {
            ...prev,
            original: {
              ...prev.original,
              delivery: {
                name: deliveryData.bestMethod || 'Доставка',
                totalCost: deliveryData.totalCost,
                numberOfTrips: deliveryData.numberOfTrips || 0,
                costPerTrip: deliveryData.costPerTrip || 0,
              },
            },
          };
        });

        console.log('✅ Доставка обновлена для нерудки:', deliveryData);
        setNotification({ type: 'success', message: 'Доставка успешно пересчитана!' });
      } else {
        console.error('❌ Некорректные данные доставки');
        setNotification({ type: 'error', message: 'Ошибка при пересчете доставки' });
      }
    } catch (error) {
      console.error('❌ Ошибка при пересчете доставки:', error);
      setNotification({ type: 'error', message: 'Ошибка при пересчете доставки' });
    }
  };

  const handleDeliveryMethodChange = (newMethod) => {
    console.log('🚚 Метод доставки изменен на:', newMethod);
    setDeliveryMethod(newMethod);
  };

  const handleSkidkaChangeDelivery = (newSkidka) => {
    console.log(`📉 handleSkidkaChangeDelivery: newSkidka=${newSkidka} (Нерудка)`);

    const originalDeliveryPrice = originalDelivery?.totalCost || 0;
    const newPrice = calculatePriceFromSkidkaOrMarzha(originalDeliveryPrice, newSkidka, per.id === 2);

    setLocalSkidkaD(newSkidka);
    setLocalDeliveryPrice(newPrice);

    // Обновляем активное предложение
    setActiveOffers(prev => {
      if (!prev.original) return prev;

      return {
        ...prev,
        original: {
          ...prev.original,
          delivery: {
            ...prev.original.delivery,
            totalCost: newPrice,
          },
        },
      };
    });
  };

  const handleGlobalSkidkaChange = (newGlobalSkidka) => {
    console.log(`🌐 handleGlobalSkidkaChange: ${newGlobalSkidka} (Нерудка)`);

    setLocalGlobalSkidka(newGlobalSkidka);

    if (!newGlobalSkidka) {
      // Если глобальная скидка убрана, восстанавливаем оригинальные цены
      const originalPrices = originalMaterials.map(material => material.pricePerOne || 0);
      setLocalPrices(originalPrices);
      setLocalSkidka(originalMaterials.map(() => ''));

      const originalDeliveryPrice = originalDelivery?.totalCost || 0;
      setLocalDeliveryPrice(originalDeliveryPrice);
      setLocalSkidkaD('');

      // Обновляем активное предложение
      setActiveOffers(prev => {
        if (!prev.original) return prev;

        const updatedMaterials = originalMaterials.map((material, index) => ({
          ...material,
          pricePerOne: originalPrices[index],
          sum: Math.ceil(originalPrices[index] * material.quantity),
        }));

        return {
          ...prev,
          original: {
            ...prev.original,
            materials: updatedMaterials,
            delivery: {
              ...prev.original.delivery,
              totalCost: originalDeliveryPrice,
            },
          },
        };
      });
    } else {
      // Применяем глобальную скидку
      const newPrices = originalMaterials.map(material =>
        calculatePriceFromSkidkaOrMarzha(material.pricePerOne || 0, newGlobalSkidka, per.id === 2)
      );
      setLocalPrices(newPrices);

      const newSkidkaValues = originalMaterials.map(() => newGlobalSkidka);
      setLocalSkidka(newSkidkaValues);

      const originalDeliveryPrice = originalDelivery?.totalCost || 0;
      const newDeliveryPrice = calculatePriceFromSkidkaOrMarzha(originalDeliveryPrice, newGlobalSkidka, per.id === 2);
      setLocalDeliveryPrice(newDeliveryPrice);
      setLocalSkidkaD(newGlobalSkidka);

      // Обновляем активное предложение
      setActiveOffers(prev => {
        if (!prev.original) return prev;

        const updatedMaterials = originalMaterials.map((material, index) => ({
          ...material,
          pricePerOne: newPrices[index],
          sum: Math.ceil(newPrices[index] * material.quantity),
        }));

        return {
          ...prev,
          original: {
            ...prev.original,
            materials: updatedMaterials,
            delivery: {
              ...prev.original.delivery,
              totalCost: newDeliveryPrice,
            },
          },
        };
      });
    }
  };

  // Тумблер «Скидка ⇄ Маржа» (как в ЖБИ). Флипает режим per и пересчитывает
  // цены материалов и доставки от ОРИГИНАЛЬНЫХ цен под новый режим, сохраняя
  // введённые проценты. Раньше эта функция была геттером значения и НИКОГДА
  // не вызывала setPer — поэтому в нерудке (и для песка) маржа не включалась.
  const skidkaOrMarzha = () => {
    const newId = per.id === 1 ? 2 : 1;
    const newText = newId === 2 ? 'Маржа' : 'Скидка';
    const isMarzha = newId === 2;

    // Цены материалов — от оригиналов под новый режим (пустой % → оригинал).
    const newPrices = originalMaterials.map((material, index) =>
      calculatePriceFromSkidkaOrMarzha(material.pricePerOne || 0, localSkidka[index], isMarzha)
    );
    setLocalPrices(newPrices);

    // Доставка — аналогично, от оригинального totalCost (только если платная).
    const originalDeliveryPrice = originalDelivery?.totalCost || 0;
    const newDeliveryPrice = originalDeliveryPrice > 0
      ? calculatePriceFromSkidkaOrMarzha(originalDeliveryPrice, localSkidkaD, isMarzha)
      : null;
    if (newDeliveryPrice !== null) setLocalDeliveryPrice(newDeliveryPrice);

    // Обновляем активное предложение тем же набором цен.
    setActiveOffers(prev => {
      if (!prev.original) return prev;

      const updatedMaterials = prev.original.materials.map((material, index) => ({
        ...material,
        pricePerOne: newPrices[index],
        sum: Math.ceil((newPrices[index] || 0) * material.quantity),
      }));

      return {
        ...prev,
        original: {
          ...prev.original,
          materials: updatedMaterials,
          delivery: (prev.original.delivery && newDeliveryPrice !== null)
            ? { ...prev.original.delivery, totalCost: newDeliveryPrice }
            : prev.original.delivery,
        },
      };
    });

    setPer({ id: newId, text: newText });
  };

  // Заглушка для кнопок (в нерудке не используются)
  const handleButtonClick = () => {
    console.log('Кнопки аналогов не используются для нерудки');
  };

  const handleFillBitrix = async (offer, offerType = 'original') => {
    console.log('📝 handleFillBitrix для нерудки:', offer, offerType);

    if (!offer || !offer.materials || offer.materials.length === 0) {
      setNotification({ type: 'error', message: 'Нет материалов для добавления в Bitrix.' });
      return;
    }

    try {
      const productsToSend = offer.materials.map((material) => ({
        bitrixId: material.bitrixId || material.name,
        price: parseFloat(material.pricePerOne) || 0,
        quantity: parseFloat(material.quantity) || 1,
        measureCode: material.measureCode || '796',
      }));

      // Добавляем доставку как отдельный продукт, если не самовывоз
      // if (offer.delivery && offer.delivery.name !== 'Самовывоз') {
      //   productsToSend.push({
      //     bitrixId: offer.delivery.bitrixId,
      //     price: parseFloat(offer.delivery.costPerTrip) || 0,
      //     quantity: parseFloat(offer.delivery.numberOfTrips) || 1,
      //   });
      // }

      if (!dealId) {
        throw new Error('ID сделки не найден');
      }

      console.log('📤 Отправка в Bitrix (Нерудка):', { dealId, products: productsToSend });

      const response = await fetch(`${PATH_TO_BACKEND}/api/sender`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dealId: dealId,
          products: productsToSend,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Ошибка при отправке в Bitrix');
      }

      setNotification({
        type: 'success',
        message: "Товары в сделке заполнены 🙂.\nДля отображения товаров - обновите страницу сделки 🔄",
      });

      setTimeout(() => setNotification(null), 5000);
    } catch (error) {
      console.error('Ошибка при заполнении сделки в Bitrix:', error);
      setNotification({
        type: 'error',
        message: `Ошибка: ${error.message}`,
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Функция для обновления способа доставки материала (только из OfferTableNonmetallic)
  const handleMaterialDeliveryMethodChange = async (materialName, newDeliveryMethodName, alternativeData) => {
    console.log('🚚 handleMaterialDeliveryMethodChange:', materialName, '->', newDeliveryMethodName, alternativeData);

    // Обновляем локальное состояние материалов
    setMaterialDeliveryMethods(prev => ({
      ...prev,
      [materialName]: newDeliveryMethodName
    }));

    // Также обновляем глобальное состояние способов доставки в ProductionsNonmetallic
    if (setNonmetallicDeliveryMethods) {
      setNonmetallicDeliveryMethods(prev => {
        const updated = {
          ...prev,
          [materialName]: newDeliveryMethodName
        };
        console.log('🚚 Updated nonmetallicDeliveryMethods:', updated);
        return updated;
      });
    }

    // Обновляем данные о доставке для материала
    if (alternativeData) {
      const deliveryData = {
        name: newDeliveryMethodName,
        totalCost: alternativeData.totalCost,
        numberOfTrips: alternativeData.numberOfTrips,
        costPerTrip: alternativeData.costPerTrip,
        distanceProdToClient: alternativeData.distanceProdToClient
      };

      setMaterialDeliveryData(prev => ({
        ...prev,
        [materialName]: deliveryData
      }));

      console.log(`🚚 Обновлены данные о доставке для ${materialName}:`, deliveryData);

      // Обновляем стоимость доставки в активном предложении
      setActiveOffers(prev => {
        if (!prev.original) return prev;

        const updatedMaterials = prev.original.materials.map(material => {
          if (material.name === materialName) {
            return {
              ...material,
              deliveryMethod: newDeliveryMethodName,
              deliveryData: deliveryData
            };
          }
          return material;
        });

        return {
          ...prev,
          original: {
            ...prev.original,
            materials: updatedMaterials
          }
        };
      });

      // Обновляем локальную цену доставки (общую)
      setLocalDeliveryPrice(alternativeData.totalCost);
    }
  };

  // Заглушки для функций добавления/удаления материалов (для нерудки упрощенно)
  const addMaterialFromTooltip = () => {
    console.log('Добавление материалов из тултипа для нерудки не реализовано');
  };

  const removeMaterial = (index, material) => {
    console.log('Удаление материала для нерудки:', index, material);

    if (!material) {
      console.warn('Материал не передан для удаления');
      return;
    }

    // Добавляем материал в список недавно удаленных
    setRecentlyRemovedMaterials(prev => {
      const removed = {
        name: material.name,
        manufacturer: material.manufacturer || null,
        source: material.source || null,
        timestamp: Date.now()
      };
      console.log('🚫 Добавлен в список недавно удаленных:', removed);
      return [...prev, removed];
    });

    // Устанавливаем флаг удаления
    setIsRemoving(true);

    // Обновляем локальное состояние материалов синхронно
    console.log('🚀 Начинаем синхронное обновление originalMaterials, текущее состояние:', originalMaterials);

    try {
      console.log('🗑️ До удаления из originalMaterials:', {
        materialToRemove: material.name,
        totalMaterials: originalMaterials.length,
      });

      // Ищем материал по имени и производству, а не по индексу
      const updatedMaterials = originalMaterials.filter(m =>
        !(m.name === material.name &&
          (m.manufacturer === material.manufacturer || m.source === material.source))
      );

      console.log('🗑️ После удаления из originalMaterials:', {
        remainingMaterials: updatedMaterials.length,
        materialNames: updatedMaterials.map(m => m.name)
      });

      // Найдем индекс удаляемого материала в originalMaterials
      const materialIndex = originalMaterials.findIndex(m =>
        m.name === material.name &&
        (m.manufacturer === material.manufacturer || m.source === material.source)
      );

      console.log('🗑️ Индекс материала в originalMaterials:', materialIndex);

      const updatedPrices = localPrices.filter((_, i) => i !== materialIndex);
      const updatedSkidkas = localSkidka.filter((_, i) => i !== materialIndex);

      // Обновляем все состояния
      setOriginalMaterials(updatedMaterials);
      setLocalPrices(updatedPrices);
      setLocalSkidka(updatedSkidkas);

      // Создаем новое активное предложение
      const newActiveOffer = {
        production: "Объединенное предложение",
        materials: updatedMaterials.map((mat, idx) => ({
          ...mat,
          pricePerOne: updatedPrices[idx] || mat.pricePerOne || 0,
          sum: Math.ceil((updatedPrices[idx] || mat.pricePerOne || 0) * mat.quantity),
          // Сохраняем индивидуальный способ доставки для каждого материала
          deliveryMethod: materialDeliveryMethods[mat.name] ||
                         nonmetallicDeliveryMethods[mat.name] ||
                         'Оптимальный маршрут',
          // Добавляем данные о доставке
          deliveryData: materialDeliveryData[mat.name] || {
            name: materialDeliveryMethods[mat.name] || 'Оптимальный маршрут',
            totalCost: 0,
            numberOfTrips: 0,
            costPerTrip: 0
          },
        })),
        delivery: {
          name: 'Самовывоз',
          totalCost: 0,
          numberOfTrips: 0,
          costPerTrip: 0
        },
        date: new Date().toLocaleDateString('ru-RU'),
        isNonmetallic: true,
      };

      setActiveOffers(prev => ({
        ...prev,
        original: newActiveOffer,
      }));

      console.log('🔄 Обновленное активное предложение после удаления:', newActiveOffer);
      console.log('🔍 Количество материалов в newActiveOffer:', newActiveOffer.materials?.length);

    } catch (error) {
      console.error('❌ Ошибка при синхронном обновлении originalMaterials:', error);
    }

    // Очищаем метод доставки для удаленного материала
    if (setNonmetallicDeliveryMethods && material.name) {
      setNonmetallicDeliveryMethods(prev => {
        const updated = { ...prev };
        delete updated[material.name];
        console.log('Очищен метод доставки для', material.name);
        return updated;
      });
    }

    // Также очищаем локальное состояние методов доставки
    setMaterialDeliveryMethods(prev => {
      const updated = { ...prev };
      delete updated[material.name];
      console.log('Очищен локальный метод доставки для', material.name);
      return updated;
    });

    // Очищаем данные о доставке
    setMaterialDeliveryData(prev => {
      const updated = { ...prev };
      delete updated[material.name];
      console.log('Очищены данные о доставке для', material.name);
      return updated;
    });

    // Обновляем selectedOffer, чтобы синхронизироваться с блоком Products
    if (setSelectedOffer) {
      setSelectedOffer(prevOffer => {
        if (!prevOffer || !prevOffer.materials) return prevOffer;

        console.log('🗑️ До удаления из selectedOffer:', {
          index,
          totalMaterials: prevOffer.materials.length,
          materialNames: prevOffer.materials.map(m => m.name),
          materialToRemove: material.name
        });

        // Ищем и удаляем материал по имени, а не по индексу
        const updatedMaterials = prevOffer.materials.filter(m =>
          !(m.name === material.name &&
            (m.manufacturer === material.manufacturer || m.source === material.source))
        );

        console.log('🗑️ После удаления из selectedOffer:', {
          remainingMaterials: updatedMaterials.length,
          materialNames: updatedMaterials.map(m => m.name)
        });

        // Если материалов не осталось, оставляем объект с пустым массивом материалов
        if (updatedMaterials.length === 0) {
          console.log('Все материалы удалены, очищаем массив материалов в selectedOffer');
          return {
            ...prevOffer,
            materials: []
          };
        }

        const updatedOffer = {
          ...prevOffer,
          materials: updatedMaterials
        };

        console.log('Обновлен selectedOffer после удаления материала:', updatedOffer);
        return updatedOffer;
      });
    }

    // Удаляем материал из Products через ref
    if (productsRef && productsRef.current && material.name) {
      console.log('🗑️ Удаляем материал из Products:', material.name);
      productsRef.current.deleteRowByName(material.name);
    }

    // Не показываем уведомление при удалении материала
    // setNotification({
    //   type: 'success',
    //   message: `Материал "${material.name}" удален из предложения`
    // });

    // Сбрасываем флаг удаления после завершения всех обновлений
    setTimeout(() => {
      setIsRemoving(false);
    }, 100);
  };

  return {
    activeOffers,
    localPrices,
    localDeliveryPrice,
    localSkidka,
    localSkidkaD,
    localGlobalSkidka,
    per,
    analogs,
    analogDeliveries,
    buttonStates,
    notification,
    tooltip,
    responseText,
    setTooltip,
    setNotification,
    handleMaterialPriceChange,
    handleSkidkaChange,
    handleDeliveryPriceChange,
    handleDeliveryUpdate,
    handleDeliveryMethodChange,
    handleSkidkaChangeDelivery,
    handleGlobalSkidkaChange,
    skidkaOrMarzha,
    handleButtonClick,
    handleFillBitrix,
    addMaterialFromTooltip,
    removeMaterial,
    // Новые функции и данные для работы с методами доставки материалов
    materialDeliveryMethods,
    materialDeliveryData,
    handleMaterialDeliveryMethodChange,
    // Функции для работы с аналогами песка
    sandAnalogs,
    isLoadingAnalogs,
    findAnalogsForSand,
    handleSelectAnalog,
  };
};
